import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button, DialogFooter, Input, Label } from '@cold-ambulance/shadcn';
import {
  FieldMessage,
  FormMessage,
  client,
  errorToFormIssue,
} from '@cold-ambulance/ui';
import { CreateDialog } from '../../components/create-dialog.tsx';
import { GeneratedPassword } from '../../components/generated-password.tsx';

const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email format' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
});

export function CreateAdmin(props: {
  trigger: React.ReactNode;
  onSave: () => void;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(
    null,
  );

  const form = useForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          const { password } = await client.request('POST /admin', {
            email: value.email,
            firstName: value.firstName,
            lastName: value.lastName,
          });
          setGeneratedPassword(password);
          toast.success('Admin created successfully');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={props.open}
      onOpenChange={props.onOpenChange}
      trigger={props.trigger}
      title="Create Admin"
      subtitle="Add a new admin to the system"
      onSave={props.onSave}
    >
      <form.Field
        name="email"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="admin@hospital.com"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
      <form.Field
        name="firstName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="أحمد"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />
      <form.Field
        name="lastName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="السعد"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      {generatedPassword && (
        <GeneratedPassword generatedPassword={generatedPassword} />
      )}
      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />
      <DialogFooter>
        <form.Subscribe
          selector={(state) => [state.isSubmitSuccessful]}
          children={([isSubmitSuccessful]) =>
            isSubmitSuccessful ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  props.onOpenChange(false);
                  setGeneratedPassword(null);
                  form.reset();
                }}
              >
                Close
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => props.onOpenChange(false)}
              >
                Cancel
              </Button>
            )
          }
        />
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}
